import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AdminTenant } from './useTenants';
import { useReleaseFleet, type ReleaseFleetResult } from './useOffboarding';
import {
  downloadTenantArchive,
  downloadTenantExport,
  EXPORT_KINDS,
  releaseBlockedReason,
  useExportStatus,
  useInvalidateExportStatus,
  type ExportKind,
  type ReleaseBlockedReason,
} from './useTenantExport';

/**
 * Release a closed customer's fleet back to inventory (slice 27) — the
 * operationally valuable half of offboarding, and the ONE part that can lose data.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THE EXPORT ORDERING TRAP — and what slice 48A changed about it.
 * ─────────────────────────────────────────────────────────────────────────────
 * Releasing sets `hidden_at` on every vehicle, and slice 26's reports apply
 * `IS_CUSTOMER_VISIBLE`, so once released those CUSTOMER-FACING reports EXCLUDE
 * the very vehicles being exported.
 *
 * Until 48A the only thing standing here was a sentence, and that sentence said
 * the loss was permanent. 🔴 IT IS NO LONGER TRUE, WHICH IS WHY THE COPY CHANGED:
 * the staff export path (`requireSuperAdmin`) can read a withdrawn vehicle's
 * history, so exporting after release still works. Ordering is now a matter of
 * convenience rather than of data loss.
 *
 * What this dialog shows is therefore a FACT, not a warning: whether an export has
 * actually been recorded for this tenant, read from the audit trail. A recited
 * sentence is not evidence; `lastExportAt` is.
 *
 * 🔴 STILL NOT A GATE. Release does not refuse when no export exists (option B),
 * because "an export was recorded" is satisfiable by a one-day CSV over a
 * three-year fleet. That refusal waits on 11b's export zip, whose row-counts-
 * equal-DB-counts criterion is the only completeness test available.
 *
 * 🔴 ONE KIND PER DOWNLOAD, AND THE UI SAYS SO. Each button exports a single
 * report. The single packaged artefact covering everything is 11b's zip and does
 * not exist yet, so nothing here may imply completeness.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * 🔴 RELEASE IS NOT CLOSE. Close blocks the view and is reversible; this returns
 * physical hardware to stock and only appears once the account is already closed.
 * The confirm asks the operator to type the tenant name so a mis-click on the
 * wrong row cannot release a fleet.
 *
 * The result is a per-vehicle TALLY, not a boolean: the Traccar seam is
 * un-transactable, so a partial run is a real outcome (some vehicles released,
 * some failed). Re-running is safe and idempotent — an already-released vehicle
 * is `skipped`, not an error — so a partial run is simply run again.
 */
export function ReleaseFleetDialog({
  tenant,
  onClose,
}: {
  tenant: AdminTenant;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(false);
  const [result, setResult] = useState<ReleaseFleetResult | null>(null);
  const release = useReleaseFleet(tenant.id);

  const status = useExportStatus(tenant.id);
  const invalidateStatus = useInvalidateExportStatus(tenant.id);
  const [kind, setKind] = useState<ExportKind>('trips');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveIncomplete, setArchiveIncomplete] = useState(false);
  /** 🔴 The server's OWN refusal reason, never a prediction of it. The gate is the
   *  single source of truth; a client that guessed would drift from it silently. */
  const [blocked, setBlocked] = useState<ReleaseBlockedReason | null>(null);

  async function runArchive() {
    setArchiving(true);
    setExportError(false);
    setArchiveIncomplete(false);
    try {
      const { complete } = await downloadTenantArchive(tenant.id);
      // An incomplete archive downloads, but it will NOT unlock release. Say so
      // now rather than letting the operator discover it after typing the name.
      setArchiveIncomplete(!complete);
      if (complete) setBlocked(null);
      invalidateStatus();
    } catch {
      setExportError(true);
    } finally {
      setArchiving(false);
    }
  }

  /** The export window: everything up to now. `from` is deliberately far back —
   *  a staff export of a departing customer wants their whole history, and the
   *  server bounds nothing by retention (that is a DISPLAY boundary). */
  async function runExport() {
    setExporting(true);
    setExportError(false);
    try {
      await downloadTenantExport(tenant.id, kind, {
        from: new Date(0).toISOString(),
        to: new Date().toISOString(),
      });
      invalidateStatus();
    } catch {
      setExportError(true);
    } finally {
      setExporting(false);
    }
  }

  const ready = confirm.trim().toLowerCase() === tenant.name.trim().toLowerCase();

  async function run() {
    if (!ready) return;
    setError(false);
    setBlocked(null);
    try {
      const r = await release.mutateAsync();
      setResult(r);
    } catch (err) {
      // A 409 is the GATE, not a failure — it means the archive is missing,
      // incomplete, stale, or the account is still open. Each has a different
      // remedy, so each gets its own words.
      const reason = releaseBlockedReason(err);
      if (reason) setBlocked(reason);
      else setError(true);
    }
  }

  const hasFailures = result !== null && result.failed.length > 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('offboard.release.title', { tenant: tenant.name })}
      className="fixed inset-0 z-50 flex items-stretch justify-center overflow-y-auto bg-black/60 p-0 sm:items-start sm:p-8"
    >
      <div className="w-full min-h-dvh rounded-none border-0 bg-raad-panel p-5 pt-[calc(1.25rem+var(--raad-safe-top))] sm:min-h-0 sm:max-w-lg sm:rounded-2xl sm:border sm:border-raad-border sm:p-6 sm:pt-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-[17px] font-semibold">
            {t('offboard.release.title', { tenant: tenant.name })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="min-h-9 rounded-lg border border-raad-border px-3 text-[12.5px] font-semibold"
          >
            {t('offboard.release.close')}
          </button>
        </div>

        {result !== null ? (
          <div>
            {/* The tally. A partial run is a real outcome, so name each bucket and
                say plainly that re-running finishes what failed. */}
            <p className="rounded-lg border border-raad-primary/40 bg-raad-primary/10 p-3 text-[12.5px]">
              {t('offboard.release.resultSummary', {
                released: result.released,
                skipped: result.skipped,
                failed: result.failed.length,
              })}
            </p>

            {result.skipped > 0 ? (
              <p className="mt-2 rounded-lg border border-raad-border bg-raad-bg/40 p-3 text-[12.5px] text-raad-muted">
                {t('offboard.release.skippedNote', { skipped: result.skipped })}
              </p>
            ) : null}

            {hasFailures ? (
              <div className="mt-2 rounded-lg border border-raad-error/40 bg-raad-error/10 p-3 text-[12.5px]">
                <p className="mb-1 font-medium">{t('offboard.release.failedHeading')}</p>
                <ul className="list-disc ps-5">
                  {result.failed.map((f) => (
                    <li key={f.vehicleId} className="num">
                      {f.vehicleId}
                      <span className="text-raad-muted"> — {f.code}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-raad-muted">{t('offboard.release.rerunHint')}</p>
              </div>
            ) : (
              <p className="mt-2 rounded-lg border border-raad-border bg-raad-bg/40 p-3 text-[12.5px] text-raad-muted">
                {t('offboard.release.allDone')}
              </p>
            )}

            {error ? (
              <p role="alert" className="mt-3 text-[12.5px] text-raad-error">
                {t('offboard.release.failed')}
              </p>
            ) : null}

            <div className="mt-4 flex justify-end gap-2">
              {hasFailures ? (
                <button
                  type="button"
                  onClick={() => void run()}
                  disabled={release.isPending}
                  className="min-h-10 rounded-lg border border-raad-border px-4 text-[13px] font-semibold disabled:opacity-40"
                >
                  {release.isPending
                    ? t('offboard.release.releasing')
                    : t('offboard.release.runAgain')}
                </button>
              ) : null}
              <button
                type="button"
                onClick={onClose}
                className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-raad-onprimary"
              >
                {t('offboard.release.doneButton')}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* 🔴 THE EXPORT STATE — a FACT read from the audit trail, not a
                recited warning. `lastExportAt === null` means no staff export has
                ever been recorded for this tenant. */}
            <div
              data-testid="export-state"
              className="rounded-lg border border-raad-warn/50 bg-raad-warn/10 p-3 text-[12.5px] font-medium"
            >
              {status.isPending ? (
                <span>{t('offboard.release.exportChecking')}</span>
              ) : status.data && status.data.lastExportAt !== null ? (
                <span data-testid="export-recorded">
                  {t('offboard.release.exportRecorded', {
                    when: new Date(status.data.lastExportAt).toLocaleString(),
                  })}
                </span>
              ) : (
                <span data-testid="export-none">{t('offboard.release.exportNone')}</span>
              )}
              <p className="mt-1 font-normal text-raad-muted">
                {t('offboard.release.exportTrap')}
              </p>
            </div>

            {/* One kind per download — the packaged all-in-one export is 11b. */}
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-raad-border bg-raad-bg/40 p-3">
              <label className="text-[12px] font-medium text-raad-muted" htmlFor="export-kind">
                {t('offboard.release.exportKindLabel')}
              </label>
              <select
                id="export-kind"
                className="min-h-9 rounded-lg border border-raad-border bg-transparent px-2 text-[12.5px]"
                value={kind}
                onChange={(e) => setKind(e.target.value as ExportKind)}
              >
                {EXPORT_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`reports.kind.${k}`, { defaultValue: k })}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={runExport}
                disabled={exporting}
                className="min-h-9 rounded-lg border border-raad-border px-3 text-[12.5px] font-semibold disabled:opacity-50"
              >
                {exporting
                  ? t('offboard.release.exporting')
                  : t('offboard.release.exportButton')}
              </button>
              <button
                type="button"
                onClick={runArchive}
                disabled={archiving}
                data-testid="archive-button"
                className="min-h-9 rounded-lg bg-raad-primary px-3 text-[12.5px] font-semibold text-raad-onprimary disabled:opacity-50"
              >
                {archiving
                  ? t('offboard.release.archiving')
                  : t('offboard.release.archiveButton')}
              </button>
              {exportError ? (
                <span role="alert" className="text-[12px] text-raad-error">
                  {t('offboard.release.exportFailed')}
                </span>
              ) : null}
            </div>
            <p className="mt-2 rounded-lg border border-raad-warn/40 bg-raad-warn/10 p-3 text-[12.5px]">
              {t('offboard.release.whatItDoes', { tenant: tenant.name })}
            </p>
            <p className="mt-2 rounded-lg border border-raad-border bg-raad-bg/40 p-3 text-[12.5px] text-raad-muted">
              {t('offboard.release.notClose')}
            </p>

            <label className="mt-4 block">
              <span className="mb-1 block text-[12px] font-medium text-raad-muted">
                {t('offboard.release.typeName', { name: tenant.name })}
              </span>
              <input
                className="w-full min-h-10 rounded-lg border border-raad-border bg-transparent px-3 text-[13.5px] outline-none focus:border-raad-primary"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoFocus
              />
            </label>

            {archiveIncomplete ? (
              <p role="alert" data-testid="archive-incomplete" className="mt-3 rounded-lg border border-raad-error/50 bg-raad-error/10 p-3 text-[12.5px] font-medium">
                {t('offboard.release.archiveIncomplete')}
              </p>
            ) : null}

            {/* 🔴 THE GATE'S OWN WORDS. Four causes, four remedies — an operator
                told only "blocked" cannot act. */}
            {blocked ? (
              <p role="alert" data-testid={`blocked-${blocked}`} className="mt-3 rounded-lg border border-raad-warn/50 bg-raad-warn/10 p-3 text-[12.5px] font-medium">
                {t(`offboard.release.blocked.${blocked}`)}
              </p>
            ) : null}

            {error ? (
              <p role="alert" className="mt-3 text-[12.5px] text-raad-error">
                {t('offboard.release.failed')}
              </p>
            ) : null}

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => void run()}
                disabled={!ready || release.isPending}
                className="min-h-10 rounded-lg bg-raad-error px-4 text-[13px] font-semibold text-white disabled:opacity-40"
              >
                {release.isPending
                  ? t('offboard.release.releasing')
                  : t('offboard.release.releaseButton')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
